import React, { useCallback, useEffect, useState } from "react";
import NewPatientAppointment from "./NewPatientAppointment";
import OldPatientAppointment from "./OldPatientAppointment";
import baseUrl from "../../../../api/baseUrl";
import { setPurpose } from "../../../../redux/appointment/appointmentAction";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../redux/store";
import socket from "../../../../api/socket";
import EventIcon from "@mui/icons-material/Event";
import PersonIcon from "@mui/icons-material/Person";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";

function NewAppointment({
  doctor,
  setNewAppointmentModal,
  newappointmentDetail,
  onsave,
}: any) {
  const timeToMinutes = useCallback((timeString: string): number => {
    const [hoursStr, minutesStr] = timeString.split(":");
    return parseInt(hoursStr) * 60 + parseInt(minutesStr);
  }, []);

  const [error, seterror] = useState(false);
  const [purposeError, setPurposeError] = useState(false);

  const subtractTimes = useCallback(
    (startTime: string, endTime: string): number => {
      return timeToMinutes(endTime) - timeToMinutes(startTime);
    },
    [timeToMinutes]
  );

  const addMinutesToTime = useCallback(
    (time: string, minutesToAdd: number): string => {
      return new Date(
        new Date(`2024-01-01T${time}`).getTime() + minutesToAdd * 60000
      ).toLocaleTimeString("en-US", {
        hour12: false,
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    []
  );

  const [selectedOption, setSelectedOption] = useState<string>("old");
  const dispatch = useDispatch();
  const purposes = useSelector((state: RootState) => state.appointment);

  const [formData, setformData] = useState({
    title: "",
    fullName: "",
    gender: "",
    mobileNo: "",
    dob: "",
    address: "",
    doctorId: newappointmentDetail?.doctorId,
    date: newappointmentDetail?.date,
    startTime: newappointmentDetail?.startTime,
    endTime: newappointmentDetail?.endTime,
    interval: "5",
    purpose: "",
    activeTime: "",
    patientId: "",
  });

  const handleOptionChange = (value: string) => {
    setSelectedOption(value);
  };

  const getPurpose = useCallback(() => {
    baseUrl
      .get("client/appointment/makeappointment/findAllPurpose")
      .then((response) => {
        dispatch(setPurpose(response.data.data, false));
      })
      .catch((error) => {
        console.log(error);
      });
  }, []);

  useEffect(() => {
    if (purposes?.purposeIsEMpty) getPurpose();
  }, [getPurpose]);

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    if (name === "interval") {
      setformData((prevFormData) => ({
        ...prevFormData,
        [name]: value,
        endTime: addMinutesToTime(formData.startTime, parseInt(value)),
      }));
    } else if (name === "endTime") {
      const differenceInMinutes = subtractTimes(formData.startTime, value);
      setformData((prevFormData) => ({
        ...prevFormData,
        [name]: value,
        interval: differenceInMinutes.toString(),
      }));
    } else {
      setformData((prevFormData: any) => ({ ...prevFormData, [name]: value }));
    }
    
    // Clear purpose error when purpose is selected
    if (name === "purpose" && value) {
      setPurposeError(false);
    }
  };

  const handleCancel = (e: any) => {
    e.preventDefault();
    setNewAppointmentModal(false);
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();
    
    // Validate purpose
    if (!formData.purpose || formData.purpose === "") {
      setPurposeError(true);
      return;
    }
    setPurposeError(false);

    const dataSocket = {
      doctorId: formData.doctorId,
      patientId: {
        _id: formData?.patientId,
        fullName: formData?.fullName,
      },
      activeTime: formData?.activeTime,
      date: formData.date,
      startTime: formData.startTime,
      endTime: formData.endTime,
      interval: formData.interval,
      purposeId: {
        purposeName: purposes?.purpose?.find(
          (item: any) => item._id === formData.purpose
        ).purposeName,
      },
    };
    socket.emit("newAppointment", dataSocket);
    seterror(false);

    if (selectedOption === "old") {
      if (formData.patientId === "") {
        seterror(true);
        return;
      }
      baseUrl
        .post(
          "/client/appointment/makeappointment/makeAppointmentByPatient",
          formData
        )
        .then((response) => {
          onsave();
          setNewAppointmentModal(false);
        })
        .catch((error) => {
          console.log(error);
        });
    } else {
      baseUrl
        .post(
          "/client/appointment/makeappointment/makeFreshAppointment",
          formData
        )
        .then((response) => {
          onsave();
          setNewAppointmentModal(false);
        })
        .catch((error) => {
          console.log(error);
        });
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden border border-slate-100 dark:border-slate-700">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary to-primaryLight px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
              <EventIcon className="text-white" />
            </div>
            <h6 className="text-xl font-bold text-white">New Appointment</h6>
          </div>
          <button
            onClick={handleCancel}
            className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center hover:bg-white/30 transition-colors"
          >
            <CloseIcon className="text-white" sx={{ fontSize: 20 }} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)] bg-white dark:bg-slate-800">
          {/* Patient Type Toggle */}
          <div className="flex gap-2 mb-6">
            <button
              type="button"
              onClick={() => handleOptionChange("old")}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-medium transition-all ${
                selectedOption === "old"
                  ? "bg-gradient-to-r from-primary to-primaryLight text-white shadow-lg shadow-primary/30"
                  : "bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600"
              }`}
            >
              <PersonIcon sx={{ fontSize: 20 }} />
              Existing Patient
            </button>
            <button
              type="button"
              onClick={() => handleOptionChange("new")}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-medium transition-all ${
                selectedOption === "new"
                  ? "bg-gradient-to-r from-primary to-primaryLight text-white shadow-lg shadow-primary/30"
                  : "bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600"
              }`}
            >
              <PersonAddIcon sx={{ fontSize: 20 }} />
              New Patient
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Patient Selection */}
            {selectedOption === "old" ? (
              <OldPatientAppointment
                setformData={setformData}
                formData={formData}
                error={error}
                setError={seterror}
              />
            ) : (
              <NewPatientAppointment
                setformData={setformData}
                formData={formData}
              />
            )}

            {/* Purpose */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-300">
                <AssignmentIcon sx={{ fontSize: 18 }} className="text-indigo-500 dark:text-indigo-400" />
                Purpose <span className="text-red-500">*</span>
              </label>
              <select
                name="purpose"
                required={true}
                value={formData.purpose}
                onChange={handleChange}
                className={`w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-700 rounded-xl focus:ring-2 focus:border-transparent outline-none transition-all text-slate-700 dark:text-slate-200 placeholder:text-slate-400 dark:placeholder:text-slate-400 ${
                  purposeError
                    ? "border-2 border-red-500 focus:ring-red-500 dark:border-red-400"
                    : "border border-slate-200 dark:border-slate-600 focus:ring-indigo-500"
                }`}
              >
                <option value="">Select Purpose</option>
                {purposes?.purpose?.map((item: any, i: number) => (
                  <option key={i} value={item?._id}>
                    {item?.purposeName}
                  </option>
                ))}
              </select>
              {purposeError && (
                <p className="text-red-600 dark:text-red-400 text-xs font-medium mt-1">
                  Please select a purpose
                </p>
              )}
            </div>

            {/* Dentist */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-300">
                <LocalHospitalIcon sx={{ fontSize: 18 }} className="text-indigo-500 dark:text-indigo-400" />
                Dentist
              </label>
              <select
                name="doctorId"
                required={true}
                value={formData.doctorId}
                onChange={handleChange}
                disabled
                className="w-full px-4 py-2.5 bg-slate-100 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl text-slate-500 dark:text-slate-300 cursor-not-allowed [color-scheme:dark]"
              >
                {doctor?.map((item: any, i: number) => (
                  <option key={i} value={item?._id}>
                    Dr. {item?.lastName}
                  </option>
                ))}
              </select>
            </div>

            {/* Date */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-300">
                <CalendarTodayIcon sx={{ fontSize: 18 }} className="text-indigo-500 dark:text-indigo-400" />
                Date
              </label>
              <input
                type="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all text-slate-700 dark:text-slate-200 [color-scheme:dark]"
              />
            </div>

            {/* Interval */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-300">
                <AccessTimeIcon sx={{ fontSize: 18 }} className="text-indigo-500 dark:text-indigo-400" />
                Duration
              </label>
              <select
                name="interval"
                value={formData.interval}
                onChange={handleChange}
                className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all text-slate-700 dark:text-slate-200 [color-scheme:dark]"
              >
                <option value="5">5 minutes</option>
                <option value="10">10 minutes</option>
                <option value="15">15 minutes</option>
                <option value="20">20 minutes</option>
                <option value="25">25 minutes</option>
              </select>
            </div>

            {/* Time */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-300">
                <AccessTimeIcon sx={{ fontSize: 18 }} className="text-indigo-500 dark:text-indigo-400" />
                Time
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="time"
                  name="startTime"
                  value={formData.startTime}
                  onChange={handleChange}
                  disabled
                  className="flex-1 px-4 py-2.5 bg-slate-100 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl text-slate-500 dark:text-slate-400 cursor-not-allowed [color-scheme:dark]"
                />
                <span className="text-slate-400 dark:text-slate-500 font-medium">to</span>
                <input
                  type="time"
                  name="endTime"
                  value={formData.endTime}
                  onChange={handleChange}
                  className="flex-1 px-4 py-2.5 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all text-slate-700 dark:text-slate-200 [color-scheme:dark]"
                />
              </div>
            </div>

          </form>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-slate-50 dark:bg-slate-800/80 border-t border-slate-100 dark:border-slate-700 flex gap-3">
          <button
            type="button"
            onClick={handleCancel}
            className="flex-1 px-6 py-3 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 rounded-xl font-medium hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors flex items-center justify-center gap-2"
          >
            <CloseIcon sx={{ fontSize: 20 }} />
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            className="flex-1 px-6 py-3 bg-gradient-to-r from-primary to-primaryLight text-white rounded-xl font-medium hover:shadow-lg hover:shadow-primary/30 transition-all flex items-center justify-center gap-2"
          >
            <SaveIcon sx={{ fontSize: 20 }} />
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

export default NewAppointment;
